import React, { useRef, useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Table, Popconfirm, Button, Input, message } from "antd";
import CommonListCard from "../../../views/components/reuseComp/CommonListCard";
import {
  useContactList,
  useDeleteContactForm,
} from "../../../hooks/api/contact/hooks";
import { IContactApiResponse } from "../../../types/api/contact";
import { QData } from "../../../types/basic-type-list";
import MyPagination from "../../components/Pagination";
import { ColumnType } from "antd/es/table";
import moment from "moment";

interface ContactListingProps {
  formType: "CONTACT" | "ENQUIRY";
  allowURLStateUpdate?: boolean;
}

const ContactListing: React.FC<ContactListingProps> = ({
  formType,
  allowURLStateUpdate = true,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [editing, setEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, refetchWithQuery, isFetching } = useContactList(formType);
  const contactList: IContactApiResponse[] = data?.data ?? [];
  console.log(contactList);
  const qData: QData = data?.qData ?? { page: 1, limit: 20 };

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // debounce
  const handleSearch = (value: string) => {
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      refetchWithQuery({
        page: 1,
        limit: qData.limit,
        firstName: value,
      });
    }, 500);
  };


  const deleteMutation = useDeleteContactForm();
  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      messageApi.success("Data Deleted successfully");
    } catch (err: any) {
      messageApi.error(err.message || "Delete failed");
    }
  };
  const columns: ColumnType<IContactApiResponse>[] = [
    {
      title: "SN",
      key: "sn",
      width: 70,
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Full Name",
      key: "fullName",
      render: (record: IContactApiResponse) =>
        formType === "CONTACT"
          ? `${record.contact?.firstName ?? ""} ${record.contact?.lastName ?? ""}`
          : (record.enquiry?.name ?? "-"),
    },
    {
      title: "Email",
      key: "email",
      render: (record: IContactApiResponse) =>
        formType === "CONTACT" && record.contact?.email ? (
          <a
            href={`mailto:${record.contact.email}`}
            className="text-blue-600 underline"
          >
            {record.contact.email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Message",
      key: "message",
      render: (record: IContactApiResponse) =>
        formType === "CONTACT"
          ? (record.contact?.message ?? "-")
          : (record.enquiry?.message ?? "-"),
    },
    {
      title: "Date",
      key: "createdAt",
      render: (record: IContactApiResponse) =>
        record?.createdAt
          ? moment(record?.createdAt).format("DD MMM YYYY, hh:mm A")
          : "-",
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (record: IContactApiResponse) => (
        <Popconfirm
          title="Are you sure to delete this record?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined className="cursor-pointer text-red-600 text-[18px]" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      {formType === "CONTACT" && (
        <div className="mb-4">
          <Input
            placeholder="Search by name..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />
        </div>
      )}

      <div className="hidden sm:block">
        <Table
          dataSource={contactList}
          columns={columns}
          rowKey={(item) => item._id}
          pagination={false}
          size="middle"
          loading={isFetching}
          scroll={{ x: true }}
          className="custom-table-class rounded-lg border border-gray-200"
          footer={() => (
            <MyPagination
              qData={qData}
              onChange={refetchWithQuery}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          )}
        />
      </div>

      <div className="sm:hidden space-y-4 mt-4">
        {contactList.length > 0 ? (
          contactList.map((item) => (
            <div
              key={item._id}
              className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-2"
            >
              <div>
                <b>Full Name:</b> {item?.contact?.firstName ?? "-"}{" "}
                {item?.contact?.lastName ?? ""}
              </div>

              <div>
                <b>Email:</b>{" "}
                {item?.contact?.email ? (
                  <a
                    href={`mailto:${item.contact.email}`}
                    className="text-blue-600 underline"
                  >
                    {item.contact.email}
                  </a>
                ) : (
                  "-"
                )}
              </div>

              <div>
                <b>Message:</b> {item?.contact?.message ?? "-"}
              </div>
              <div>
                <b>Date:</b> {moment(item?.createdAt).format("DD MMM YYYY, hh:mm A")}
              </div>

              <div className="flex justify-end gap-4 text-primary text-xl mt-3">
                <Popconfirm
                  title="Are you sure to delete this record?"
                  onConfirm={() => item?._id && handleDelete(item._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined className="cursor-pointer text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center py-6 text-gray-500">No leads found</p>
        )}
      </div>
    </div>
  );
};

export default ContactListing;
